import { contactApis } from '@apis/contactApis';
import { contactApis as dashboardContactApis } from '@apis/dashboard/contactApis';
import { routePlanApis as dashboardRoutePlanApis } from '@apis/dashboard/routePlanApis';
import { fcApis } from '@apis/fcApis';
import { routePlanApis } from '@apis/routePlanApis';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { fakeReceivers } from '@data/contacts';
import { useMutation } from '@hooks/useMutation';
import {
  BookingInquiryForm,
  CargosByPickupAddress,
  CargosByPort,
  PostBookingInquiry,
  TradeCondition,
} from '@models/BookingInquiry';
import { SimulatorResultCities } from '@models/RoutePlan';
import { formStrings } from '@utils/forms';
// import type { Company } from '@models/Company';
import { optionalToUndefined } from '@utils/types';
import type { Either } from 'fp-ts/lib/Either';
import { uniq } from 'lodash-es';

export interface ResolvePostBookingInquiryError {
  shipper?: string;
  receiver?: string;
  cargos_port_name?: string;
  move_in_date?: string;
  pickup_date?: string;
  invalidChinaZipcodes?: string[];
}

export interface ResolvePostBookingInquiryResult {
  postBookingInquiry: PostBookingInquiry;
  simulatorResultCities: SimulatorResultCities;
}

export const useResolvePostBookingInquiry = () => {
  return useMutation(
    async (input: {
      owner: number;
      form: BookingInquiryForm;
      isDashboard: boolean;
    }): Promise<Either<ResolvePostBookingInquiryError, ResolvePostBookingInquiryResult>> => {
      const { owner, form, isDashboard } = input;

      const errors: ResolvePostBookingInquiryError = {};

      const simulatorResultCities: SimulatorResultCities = {
        receiver_city: '',
      };

      const [
        shipperResponse,
        consigneeResponse,
        receiverResponse,
        pickupAddressesResponse,
        routePlanResponse,
      ] = await Promise.all([
        form.shipper_input
          ? Promise.resolve({ 200: form.shipper_input, 404: null })
          : isDashboard
          ? dashboardContactApis.get('/dashboard/contact/{id}/', {
              path: {
                id: form.shipper!,
              },
            })
          : contactApis.get('/contact/{id}/', {
              path: {
                id: form.shipper!,
              },
            }),
        form.consignee_input
          ? Promise.resolve({ 200: form.consignee_input, 404: null })
          : isDashboard
          ? dashboardContactApis.get('/dashboard/contact/{id}/', {
              path: {
                id: form.consignee!,
              },
            })
          : contactApis.get('/contact/{id}/', {
              path: {
                id: form.consignee!,
              },
            }),
        form.receiver_input
          ? Promise.resolve({ 200: form.receiver_input, 404: null })
          : isDashboard
          ? fakeReceivers.find((c) => c.id === form.receiver)
            ? Promise.resolve({
                200: optionalToUndefined(fakeReceivers.find((c) => c.id === form.receiver)!),
                404: null,
              })
            : dashboardContactApis.get('/dashboard/contact/{id}/', {
                path: {
                  id: form.receiver!,
                },
              })
          : contactApis.get('/contact/{id}/', {
              path: {
                id: form.receiver!,
              },
            }),
        form.shipper_input
          ? Promise.resolve({ 200: [], 404: null })
          : isDashboard
          ? dashboardContactApis.get('/dashboard/contact/', {
              query: { owner_id: form.shipper },
            })
          : contactApis.get('/contact/', {
              query: { owner_id: form.shipper },
            }),
        (form.route_plan ?? 0) > 0
          ? isDashboard
            ? dashboardRoutePlanApis.get('/dashboard/route-plan/{id}/', {
                path: { id: form.route_plan! },
              })
            : routePlanApis.get('/route-plan/{id}/', { path: { id: form.route_plan! } })
          : Promise.resolve({ 200: undefined }),
      ]);

      if (shipperResponse[404]) {
        errors.shipper = bookingInquiryStrings.notFound;
      }

      if (consigneeResponse[404]) {
        errors.receiver = bookingInquiryStrings.notFound;
      }

      if (receiverResponse[404]) {
        errors.receiver = bookingInquiryStrings.notFound;
      }

      if (!receiverResponse[200]?.zip_code) {
        errors.receiver = bookingInquiryStrings.receiverMissingZipcode;
      } else {
        const receiverZipcodeResponse = await fcApis.get('/fc/pref-and-city-by-zipcode/', {
          query: { zipcode: receiverResponse[200]?.zip_code },
        });

        if (!receiverZipcodeResponse[200]) {
          errors.receiver = bookingInquiryStrings.receiverWrongZipcode;
        } else {
          simulatorResultCities.receiver_city = receiverZipcodeResponse[200].pref;
        }
      }

      const pickupAddresses = pickupAddressesResponse[200] ?? [];

      if (form.has_pickups) {
        const pickupAddressesToCheck = [
          ...pickupAddresses.filter((p) => form.cargos_pickup_addresses.includes(p.id)),
          ...form.cargos_pickup_address_inputs.map((p) => p.contact),
        ];
        const pickupZipcodes = uniq(pickupAddressesToCheck.map((p) => p.zip_code)).filter(
          (p) => p.length > 0,
        );

        const pickupZipcodeResponses = await Promise.all(
          pickupZipcodes.map((zipcode) =>
            fcApis.get('/fc/prov-and-city-by-zipcode/', {
              query: { zipcode },
            }),
          ),
        );

        for (let i = 0; i < pickupZipcodes.length; i++) {
          if (
            !pickupZipcodeResponses[i][200] ||
            pickupZipcodeResponses[i][200]?.list.length === 0
          ) {
            errors.invalidChinaZipcodes = [
              ...(errors.invalidChinaZipcodes ?? []),
              pickupZipcodes[i],
            ];
          }
        }
      }

      const resolveCargosByPickupAddress = (): CargosByPickupAddress => {
        const result: CargosByPickupAddress = [];

        if (!form.pickup_date) {
          errors.pickup_date = formStrings.notEmpty(bookingInquiryStrings.pickupDate);
        }

        if (form.shipper) {
          for (const id of uniq(form.cargos_pickup_addresses)) {
            const pickupAddress = pickupAddresses.find((v) => v.id === id);

            if (pickupAddress) {
              const cargos = form.cargos2.filter(
                (c) => form.cargos_pickup_addresses[c.pickup_address_index! - 1] === id,
              );

              if (cargos.length) {
                result.push({
                  pickup_address: pickupAddress,
                  cargos,
                });
              }
            } else {
              console.warn(`Pickup address ${id} is not found. `);
            }
          }
        } else {
          for (let i = 0; i < form.cargos_pickup_address_inputs.length; i++) {
            const cargos = form.cargos2.filter((c) => c.pickup_address_index === i + 1);
            if (cargos.length) {
              result.push({
                pickup_address: form.cargos_pickup_address_inputs[i].contact,
                cargos,
              });
            }
          }
        }

        return result;
      };

      const resolveCargosByPort = (): CargosByPort => {
        if (form.cargos_port_name.trim() === '') {
          errors.cargos_port_name = formStrings.shouldSelect;
        }

        if (!form.move_in_date) {
          errors.move_in_date = formStrings.notEmpty(bookingInquiryStrings.moveInDate);
        }

        return [
          {
            port_name: routePlanResponse[200]?.receiving_city ?? form.cargos_port_name,
            cargos: form.cargos2,
          },
        ];
      };

      const postBookingInquiry: PostBookingInquiry = {
        shipper: shipperResponse[200]!,
        consignee: consigneeResponse[200]!,
        receiver: receiverResponse[200]!,
        has_pickups: form.has_pickups,
        cargos: form.has_pickups
          ? {
              cargos_by_pickup_address: resolveCargosByPickupAddress(),
              cargos_by_port: undefined,
            }
          : {
              cargos_by_pickup_address: undefined,
              cargos_by_port: resolveCargosByPort(),
            },
        trade_condition: form.trade_condition as TradeCondition,
        customer_no: form.customer_no,
        special_note: form.special_note,
        is_caution_checked: form.is_caution_checked.length > 0,
        preferred_arrival_date: form.preferred_arrival_date,
        use_trading_agency: form.use_trading_agency,
        trading_agency_payer: form.trading_agency_payer,
        file: undefined,
        can_views: [],
        is_invoice_done: false,
        is_payment_slip_done: false,
        source: isDashboard ? 'admin' : 'client',
        owner,
        route_plan: routePlanResponse[200]!,
        route_plan_simulator_result: form.route_plan_simulator_result,
        move_in_date: form.move_in_date,
        pickup_date: form.pickup_date,
        use_rcep: form.use_rcep,
        customs_instruction: {
          ...form.customs_instruction,
          special_note: form.customs_instruction.special_note.map((note) => ({
            ...note,
            selected: form.customs_instruction.special_note_titles.includes(note.title),
          })),
        },
        delivery_instruction: form.delivery_instruction,
        has_special_note: form.has_special_note,
      };

      if (Object.keys(errors).length) {
        return {
          _tag: 'Left',
          left: errors,
        };
      }

      return {
        _tag: 'Right',
        right: {
          postBookingInquiry,
          simulatorResultCities,
        },
      };
    },
  );
};
